import type { Viewport } from './types';

/**
 * Bornes par défaut pour le zoom.
 * - min 0.1 = vue lointaine pour grandes œuvres (500×500)
 * - max 32 = zoom proche pour édition perle-par-perle
 */
export const DEFAULT_MIN_SCALE = 0.1;
export const DEFAULT_MAX_SCALE = 32;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Translate le viewport.
 * `dx` et `dy` sont en coordonnées **monde** (pixels avant scale).
 *
 * Pour panner depuis un drag écran de Δscreen, passer `Δscreen / scale`.
 */
export function pan(v: Viewport, dx: number, dy: number): Viewport {
  return { offsetX: v.offsetX + dx, offsetY: v.offsetY + dy, scale: v.scale };
}

/**
 * Zoome le viewport en gardant le point sous `anchorScreenX/Y` fixe à l'écran.
 *
 * @param factor multiplicateur du scale (>1 zoom in, <1 zoom out)
 * @param anchorScreenX coord X écran de l'ancre (souvent : position de la souris ou centre du pinch)
 * @param anchorScreenY coord Y écran de l'ancre
 * @param opts.minScale / maxScale pour borner le zoom
 */
export function zoom(
  v: Viewport,
  factor: number,
  anchorScreenX: number,
  anchorScreenY: number,
  opts: { minScale?: number; maxScale?: number } = {},
): Viewport {
  const minS = opts.minScale ?? DEFAULT_MIN_SCALE;
  const maxS = opts.maxScale ?? DEFAULT_MAX_SCALE;
  const newScale = clamp(v.scale * factor, minS, maxS);
  if (newScale === v.scale) return v;

  // Invariant : le point monde sous l'ancre reste fixe à l'écran.
  // screen = (world + offset) * scale  ⇒  world = anchor/scale - offset
  // Après zoom : newOffset = anchor/newScale - world
  const worldX = anchorScreenX / v.scale - v.offsetX;
  const worldY = anchorScreenY / v.scale - v.offsetY;
  return {
    offsetX: anchorScreenX / newScale - worldX,
    offsetY: anchorScreenY / newScale - worldY,
    scale: newScale,
  };
}

/** Conversion coord écran → coord monde. */
export function screenToWorld(
  v: Viewport,
  screenX: number,
  screenY: number,
): { worldX: number; worldY: number } {
  return {
    worldX: screenX / v.scale - v.offsetX,
    worldY: screenY / v.scale - v.offsetY,
  };
}

/** Conversion coord monde → coord écran. */
export function worldToScreen(
  v: Viewport,
  worldX: number,
  worldY: number,
): { screenX: number; screenY: number } {
  return {
    screenX: (worldX + v.offsetX) * v.scale,
    screenY: (worldY + v.offsetY) * v.scale,
  };
}

/**
 * Calcule l'intervalle de cellules visibles à l'écran, **clampé aux dimensions du pattern**.
 *
 * Sert au viewport culling : on ne dessine que les cellules dont au moins un pixel
 * est dans la zone visible. Réduit drastiquement le coût quand on zoome (90% des
 * cellules d'une grande œuvre peuvent être hors viewport).
 *
 * Retourne des indices inclusifs (`minCol..maxCol`, `minRow..maxRow`). Si la grille
 * est entièrement hors écran, retourne `maxCol < minCol` (la boucle ne s'exécute pas).
 */
export function getVisibleCellBounds(
  v: Viewport,
  screenWidth: number,
  screenHeight: number,
  cellSize: number,
  patternWidth: number,
  patternHeight: number,
): { minCol: number; maxCol: number; minRow: number; maxRow: number } {
  const topLeft = screenToWorld(v, 0, 0);
  const bottomRight = screenToWorld(v, screenWidth, screenHeight);

  const rawMinCol = Math.floor(topLeft.worldX / cellSize);
  const rawMaxCol = Math.ceil(bottomRight.worldX / cellSize);
  const rawMinRow = Math.floor(topLeft.worldY / cellSize);
  const rawMaxRow = Math.ceil(bottomRight.worldY / cellSize);

  return {
    minCol: Math.max(0, rawMinCol),
    maxCol: Math.min(patternWidth - 1, rawMaxCol),
    minRow: Math.max(0, rawMinRow),
    maxRow: Math.min(patternHeight - 1, rawMaxRow),
  };
}

/**
 * Convertit un pixel écran en cellule (col, row) de la grille.
 * Retourne `null` si hors-bornes.
 */
export function cellAt(
  v: Viewport,
  screenX: number,
  screenY: number,
  cellSize: number,
  patternWidth: number,
  patternHeight: number,
): { col: number; row: number } | null {
  const { worldX, worldY } = screenToWorld(v, screenX, screenY);
  const col = Math.floor(worldX / cellSize);
  const row = Math.floor(worldY / cellSize);
  if (col < 0 || col >= patternWidth || row < 0 || row >= patternHeight) return null;
  return { col, row };
}

/**
 * Centre le viewport sur une zone monde donnée et ajuste le scale pour qu'elle
 * tienne dans le viewport avec une marge. Utile pour "fit to screen".
 */
export function fitToScreen(
  worldWidth: number,
  worldHeight: number,
  screenWidth: number,
  screenHeight: number,
  margin: number = 0.92,
): Viewport {
  const scaleX = (screenWidth * margin) / worldWidth;
  const scaleY = (screenHeight * margin) / worldHeight;
  const scale = Math.min(scaleX, scaleY);
  // Centre la zone monde dans le viewport
  const offsetX = (screenWidth / scale - worldWidth) / 2;
  const offsetY = (screenHeight / scale - worldHeight) / 2;
  return { offsetX, offsetY, scale };
}
